import c from 'classnames'
import { addDays, addMonths, format } from 'date-fns'
import { es } from 'date-fns/locale'
import { DateTime } from 'luxon'
import React, { useCallback, useMemo, useState } from 'react'
import { useCardTransition } from '../../../providers/cardTransition/cardTransition'
import { VARIANTS } from '../../../types/Components.types'
import Button from '../button/Button'
import styles from './MonthPicker.module.css'

export interface IMonthPickerProps {
  className?: string
  /**
   * Initial selected date
   */
  initialDate?: Date | null
  /**
   * Callback when month is confirmed
   */
  onMonthConfirm: (month: number) => void
  /**
   * Minimum selectable date
   * @default tomorrow
   */
  minDate?: Date | DateTime | 'today' | 'tomorrow'
  /**
   * Maximum selectable date
   * @default 2 months from today
   */
  maxDate?: Date | DateTime
  /**
   * Whether to close the card immediately on month selection (without confirmation button)
   * @default false
   */
  closeOnSelect?: boolean
  /**
   * Whether to show the confirm button
   * @default true
   */
  showConfirmButton?: boolean
  /**
   * Confirm button text
   * @default "Confirmar"
   */
  confirmButtonText?: string
}

const MONTHS = Array.from({ length: 12 }, (_, i) => i)

const resolveDate = (date: Date | DateTime | 'today' | 'tomorrow' | undefined, defaultDate: Date): Date => {
  if (!date) return defaultDate
  if (date === 'today') return new Date()
  if (date === 'tomorrow') return addDays(new Date(), 1)
  if (date instanceof DateTime) return date.toJSDate()
  return date
}

const MonthPicker = ({
  className,
  initialDate = null,
  onMonthConfirm,
  minDate,
  maxDate,
  closeOnSelect = false,
  showConfirmButton = true,
  confirmButtonText = 'Confirmar',
}: IMonthPickerProps) => {
  const { closeCard } = useCardTransition()
  const currentDate = new Date()
  const currentYear = currentDate.getFullYear()
  const currentMonth = currentDate.getMonth()

  const resolvedMinDate = useMemo(() => resolveDate(minDate, addDays(new Date(), 1)), [minDate])
  const resolvedMaxDate = useMemo(() => resolveDate(maxDate, addMonths(new Date(), 2)), [maxDate])

  const [selected, setSelected] = useState<{ year: number; month: number } | null>(() => {
    if (initialDate) {
      return { year: initialDate.getFullYear(), month: initialDate.getMonth() }
    }
    return null
  })

  const displayYear = initialDate?.getFullYear() || currentYear

  const minYear = resolvedMinDate.getFullYear()
  const minMonth = resolvedMinDate.getMonth()
  const maxYear = resolvedMaxDate.getFullYear()
  const maxMonth = resolvedMaxDate.getMonth()

  const isMonthDisabled = useCallback(
    (month: number) => {
      if (!displayYear) return false
      if (displayYear < minYear || (displayYear === minYear && month < minMonth)) {
        return true
      }
      if (displayYear > maxYear || (displayYear === maxYear && month > maxMonth)) {
        return true
      }
      return false
    },
    [displayYear, minYear, minMonth, maxYear, maxMonth],
  )

  const handleMonthClick = useCallback(
    (month: number) => {
      if (isMonthDisabled(month)) return

      const newSelection = { year: displayYear, month }
      setSelected(newSelection)

      if (closeOnSelect) {
        onMonthConfirm(month)
        closeCard?.()
      }
    },
    [displayYear, closeOnSelect, onMonthConfirm, closeCard, isMonthDisabled],
  )

  const handleConfirmClick = useCallback(() => {
    if (selected) {
      onMonthConfirm(selected.month + 1)
      closeCard?.()
    }
  }, [closeCard, onMonthConfirm, selected])

  const isConfirmDisabled = !selected

  const getMonthName = (month: number) => {
    const date = new Date(displayYear, month, 1)
    return format(date, 'MMM', { locale: es })
  }

  return (
    <div className={c(styles.container, className)}>
      <div className={styles.content}>
        <div className={styles.monthPicker}>
          <div className={styles.grid}>
            {MONTHS.map((month) => {
              const isSelected = selected?.year === displayYear && selected?.month === month
              const isCurrent = currentYear === displayYear && currentMonth === month
              const isDisabled = isMonthDisabled(month)

              return (
                <button
                  key={month}
                  type="button"
                  className={c(styles.monthButton, {
                    [styles.selected]: isSelected,
                    [styles.currentMonth]: isCurrent && !isSelected,
                  })}
                  onClick={() => handleMonthClick(month)}
                  disabled={isDisabled}
                  aria-label={getMonthName(month)}
                >
                  {getMonthName(month)}
                </button>
              )
            })}
          </div>
        </div>
      </div>

      {showConfirmButton && (
        <div className={styles.actions}>
          <Button
            variant={VARIANTS.primary}
            className={styles.action}
            onClick={handleConfirmClick}
            disabled={isConfirmDisabled}
          >
            {confirmButtonText}
          </Button>
        </div>
      )}
    </div>
  )
}

export default React.memo(MonthPicker)
